import { useMemo } from 'react';
import { useAuthStore } from '@store/authStore';
import {
  canAccessModule,
  canPerform,
  getPermission,
  getScope,
} from '@/lib/permissions';

/**
 * React hook bridging the permissions matrix to the auth store.
 * Reads `userProfile.role` and returns memoized helpers bound to that role.
 *
 * @returns {{
 *   role: string | null,
 *   can: (module: string, action: string) => boolean,
 *   canAccess: (module: string) => boolean,
 *   scope: (module: string) => string,
 *   permission: (module: string) => object,
 * }}
 */
export const usePermissions = () => {
  const userProfile = useAuthStore((state) => state.userProfile);
  const role = userProfile?.role || null;

  return useMemo(
    () => ({
      role,
      can: (module, action) => canPerform(role, module, action),
      canAccess: (module) => canAccessModule(role, module),
      scope: (module) => getScope(role, module),
      permission: (module) => getPermission(role, module),
    }),
    [role]
  );
};

export default usePermissions;
